import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import { Link } from 'gatsby';
import '../design/design-guidelines-styles.css';
import { ColorSwatch } from '../../components/ColorSwatch';
import { magma } from 'react-magma-dom';
import { LeadParagraph } from '../../components/LeadParagraph';
export const _frontmatter = {
  "pageTitle": "Color Design Guidelines",
  "title": "Color",
  "order": 2
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

<h1><a parentName="h1" {...{
        "href": "'design'"
      }}>{`Color`}</a></h1>
<p>{`Color helps express hierarchy, establish brand presence, give meaning, and
indicate element states.`}</p>
<h2>{`Color Palette`}</h2>
<p><em parentName="p">{`*`}{` Contrast ratio on each color is based on a white background. `}</em></p>
<h3>{`Primary`}</h3>
<p>{`The primary color is the main brand color. A brand color can be used in key moments, in ways that associate that color with specific actions and information.`}</p>
<div className="swatch-rows">
  <ColorSwatch color={magma.colors.primary} mdxType="ColorSwatch">
    <span>
      <strong>primary</strong>
    </span>
    <span className="color-hex">{magma.colors.primary}</span>
    <span>rgb(0,98,152)</span>
    <span>6.56:1</span>
  </ColorSwatch>
</div>
<h3>{`Focus`}</h3>
<p>{`The focus color is used to visually highlight elements that are in focus on the UI. This color compliments the palette, while standing out against light colors to make the focus states visually distinct. This color will meet the 3:1 contrast ratio required as long as you use neutral07 or neutral08 as your background.
If you are using dark backgrounds, then the focus color should use the focusInverse color instead, which is a semi-transparent white.`}</p>
<div className="swatch-rows">
  <ColorSwatch color={magma.colors.focus} mdxType="ColorSwatch">
    <span>
      <strong>focus</strong>
    </span>
    <span className="color-hex">{magma.colors.focus}</span>
    <span>rgb(2, 126, 225)</span>
    <span>contrast ratio: 4.13:1</span>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.focusInverse} mdxType="ColorSwatch">
    <span>
      <strong>focusInverse</strong>
    </span>
    <span>rgba(255, 255, 255, 0.7)</span>
    <span>contrast ratio: 1.0:1</span>
  </ColorSwatch>
</div>
<h3>{`Foundation`}</h3>
<p>{`Our blues are just like us: confident, cool and collected. They also, just like us, play nicely with others. Go ahead and use multiple shades of blue in webpages to show off the dynamic palette.`}</p>
<div className="swatch-rows">
  <ColorSwatch color={magma.colors.foundation} mdxType="ColorSwatch">
    <span>
      <strong>foundation</strong>
    </span>
    <span className="color-hex">{magma.colors.foundation}</span>
    <span>rgb(0,38,62)</span>
    <span>contrast ratio: 15.59:1</span>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.foundation02} mdxType="ColorSwatch">
    <span>
      <strong>foundation02</strong>
    </span>
    <span className="color-hex">{magma.colors.foundation02}</span>
    <span>rgb(0,56,101)</span>
    <span>contrast ratio: 11.97:1</span>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.foundation03} mdxType="ColorSwatch">
    <span>
      <strong>foundation03</strong>
    </span>
    <span className="color-hex">{magma.colors.foundation03}</span>
    <span>rgb(0,169,224)</span>
    <span>contrast ratio: 2.70:1</span>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.foundation04} mdxType="ColorSwatch">
    <span>
      <strong>foundation04</strong>
    </span>
    <span className="color-hex">{magma.colors.foundation04}</span>
    <span>rgb(113,197,232)</span>
    <span>contrast ratio: 1.93:1</span>
  </ColorSwatch>
</div>
<h3>{`Feedback`}</h3>
<p>{`Feedback colors should be used to reinforce positive or negative feedback to users.`}</p>
<div className="swatch-rows">
  <ColorSwatch color={magma.colors.success} mdxType="ColorSwatch">
    <span>
      <strong>success</strong>
    </span>
    <span className="color-hex">{magma.colors.success}</span>
    <span>rgb(58,130,0)</span>
    <span>contrast ratio: 4.80:1</span>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.success02} mdxType="ColorSwatch">
    <span>
      <strong>success02</strong>
    </span>
    <span className="color-hex">{magma.colors.success02}</span>
    <span>rgb(72,162,0)</span>
    <span>contrast ratio: 3.25:1</span>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.danger} mdxType="ColorSwatch">
    <span>
      <strong>danger</strong>
    </span>
    <span className="color-hex">{magma.colors.danger}</span>
    <span>rgb(198,29,35)</span>
    <span>contrast ratio: 5.83:1</span>
  </ColorSwatch>
</div>
<h3>{`Pops`}</h3>
<p>{`The goal of our pop colors is to create some buzzy vibrations with our blues. Let’s try to just use one pop (or two pops that are next to each other in the palette) at a time to keep the Cengage vibe going strong. Do not create tonal versions of these colors.`}</p>
<div className="swatch-rows">
  <ColorSwatch color={magma.colors.pop} mdxType="ColorSwatch">
    <span>
      <strong>pop</strong>
    </span>
    <span className="color-hex">{magma.colors.pop}</span>
    <span>rgb(224,0,77)</span>
    <span>contrast ratio: 4.91:1</span>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.pop02} mdxType="ColorSwatch">
    <span>
      <strong>pop02</strong>
    </span>
    <span className="color-hex">{magma.colors.pop02}</span>
    <span>rgb(252,76,2)</span>
    <span>contrast ratio: 3.40:1</span>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.pop03} mdxType="ColorSwatch">
    <span>
      <strong>pop03</strong>
    </span>
    <span className="color-hex">{magma.colors.pop03}</span>
    <span>rgb(242,169,0)</span>
    <span>contrast ratio: 2.0:1</span>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.pop04} mdxType="ColorSwatch">
    <span>
      <strong>pop04</strong>
    </span>
    <span className="color-hex">{magma.colors.pop04}</span>
    <span>rgb(255,199,44)</span>
    <span>contrast ratio: 1.56:1</span>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.pop05} mdxType="ColorSwatch">
    <span>
      <strong>pop05</strong>
    </span>
    <span className="color-hex">{magma.colors.pop05}</span>
    <span>rgb(146, 39, 143)</span>
    <span>contrast ratio: 7.21:1</span>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.pop06} mdxType="ColorSwatch">
    <span>
      <strong>pop06</strong>
    </span>
    <span className="color-hex">{magma.colors.pop06}</span>
    <span>rgb(0,122,109)</span>
    <span>contrast ratio: 5.24:1</span>
  </ColorSwatch>
</div>
<h3>{`Neutral`}</h3>
<p>{`Sometimes, going subtle makes a statement, too. Use our neutrals for the ultimate in legibility or to provide a rest from the buzzy bolds.`}</p>
<div className="swatch-rows">
  <ColorSwatch color={magma.colors.neutral} mdxType="ColorSwatch">
    <span>
      <strong>neutral</strong>
    </span>
    <span className="color-hex">{magma.colors.neutral}</span>
    <span>rgb(63,63,63)</span>
    <span>contrast ratio: 10.53:1</span>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.neutral02} mdxType="ColorSwatch">
    <span>
      <strong>neutral02</strong>
    </span>
    <span className="color-hex">{magma.colors.neutral02}</span>
    <span>rgb(87,87,87)</span>
    <span>contrast ratio: 7.22:1</span>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.neutral03} mdxType="ColorSwatch">
    <span>
      <strong>neutral03</strong>
    </span>
    <span className="color-hex">{magma.colors.neutral03}</span>
    <span>rgb(112,112,112)</span>
    <span>contrast ratio: 4.95:1</span>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.neutral04} mdxType="ColorSwatch">
    <span>
      <strong>neutral04</strong>
    </span>
    <span className="color-hex">{magma.colors.neutral04}</span>
    <span>rgb(143,143,143)</span>
    <span>contrast ratio: 3.23:1</span>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.neutral05} mdxType="ColorSwatch">
    <span>
      <strong>neutral05</strong>
    </span>
    <span className="color-hex">{magma.colors.neutral05}</span>
    <span>rgb(191,191,191)</span>
    <span>contrast ratio: 1.83:1</span>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.neutral06} mdxType="ColorSwatch">
    <span>
      <strong>neutral06</strong>
    </span>
    <span>{magma.colors.neutral06}</span>
    <span>rgb(223,223,223)</span>
    <span>contrast ratio: 1.33:1</span>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.neutral07} mdxType="ColorSwatch">
    <span>
      <strong>neutral07</strong>
    </span>
    <span className="color-hex">{magma.colors.neutral07}</span>
    <span>rgb(247,247,247)</span>
    <span>contrast ratio: 1.07:1</span>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.neutral08} mdxType="ColorSwatch">
    <span>
      <strong>neutral08</strong>
    </span>
    <span className="color-hex">{magma.colors.neutral08}</span>
    <span>rgb(255,255,255)</span>
    <span>contrast ratio: 1.0:1</span>
  </ColorSwatch>
</div>
<h3>{`Inverse Colors`}</h3>
<p>{`The inverse colors are designed to be used against dark backgrounds.`}</p>
<div className="swatch-rows">
  <ColorSwatch color={magma.colors.primaryInverse} mdxType="ColorSwatch">
    <span>
      <strong>primaryInverse</strong>
    </span>
    <span className="color-hex">{magma.colors.primaryInverse}</span>
    <span>rgb(112 205 255)</span>
    <span>contrast ratio: 5.95:1 (against #3F3F3F)</span>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.successInverse} mdxType="ColorSwatch">
    <span>
      <strong>successInverse</strong>
    </span>
    <span className="color-hex">{magma.colors.successInverse}</span>
    <span>rgb(145,207,96)</span>
    <span>contrast ratio: 5.66:1 (against #3F3F3F)</span>
  </ColorSwatch>
  <ColorSwatch color={magma.colors.dangerInverse} mdxType="ColorSwatch">
    <span>
      <strong>dangerInverse</strong>
    </span>
    <span className="color-hex">{magma.colors.dangerInverse}</span>
    <span>rgb(245,146,149)</span>
    <span>contrast ratio: 4.72:1 (against #3F3F3F)</span>
  </ColorSwatch>
</div>
<hr></hr>
<h2>{`Principles`}</h2>
<h3>{`Consistent`}</h3>
<p>{`Color should be applied throughout a UI consistently and be compatible with the brand it represents.`}</p>
<h3>{`Hierarchical`}</h3>
<p>{`Color indicates which elements are interactive, how they relate to other elements, and their level of prominence. Important elements should stand out the most.`}</p>
<h3>{`Legible`}</h3>
<p>{`Text and important elements, like icons, should meet legibility standards when appearing on colored backgrounds, across all screen and device types.`}</p>
<h3>{`Expressive`}</h3>
<p>{`Reinforce your brand by showing brand colors at memorable moments that reinforce your brand’s style.`}</p>
<hr></hr>
<h2>{`Accessibility`}</h2>
<p>{`All text should be legible and meet accessibility standards. `}<a parentName="p" {...{
        "href": "https://www.w3.org/TR/WCAG21/#contrast-minimum"
      }}>{`The Web Content Accessibility Guidelines (WCAG 2.1)`}</a>{` level AA requires a 4.5:1 color contrast between text and background for normal text, and 3:1 to large text. Large text is at least 18 point or 14 point bold. Additionally, any non-text visual information provided that is necessary for a user to identify that a control is present and how to operate it must have a minimum 3:1 contrast ratio with the adjacent colors.`}</p>
<p>{`The contrast ratio displayed on each color above is based on that color on a white background. If you are using a background color other than white, make sure you check the contrast ratio using a tool like the one found at `}<a parentName="p" {...{
        "href": "https://webaim.org/resources/contrastchecker/"
      }}>{`webaim.org`}</a>{`*`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      